import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  Flame, 
  Eye,
  Music,
  Calendar,
  Sparkles,
  Crown,
  Award
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const TrendingPage = () => {
  const [rankings, setRankings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('weekly');
  const [activeTab, setActiveTab] = useState('weekly');

  useEffect(() => {
    loadTrending();
    loadStats();
  }, [period]);

  const loadTrending = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/trending/top50?period=${period}&limit=50`);
      setRankings(response.data.rankings || []);
    } catch (error) {
      console.error('Error loading trending:', error);
      toast.error('Erro ao carregar tendências');
      setRankings([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/trending/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-5 h-5 text-green-500" />;
      case 'down':
        return <TrendingDown className="w-5 h-5 text-red-500" />;
      case 'new':
        return <Sparkles className="w-5 h-5 text-yellow-500" />;
      default:
        return <Minus className="w-5 h-5 text-gray-400" />;
    }
  };

  const getTrendBadge = (trend, growth) => {
    if (trend === 'new') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
          <Sparkles className="w-3 h-3" />
          NOVA
        </span>
      );
    }
    
    if (Math.abs(growth) < 5) {
      return null;
    }

    const color = growth > 0 ? 'green' : 'red';
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-${color}-100 text-${color}-800`}>
        {growth > 0 ? '+' : ''}{growth.toFixed(0)}%
      </span>
    );
  };

  const getRankBadge = (rank) => {
    if (rank === 1) {
      return (
        <div className="absolute -top-2 -right-2 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full p-2 shadow-lg">
          <Crown className="w-5 h-5 text-white" />
        </div>
      );
    }
    if (rank <= 3) {
      return (
        <div className="absolute -top-2 -right-2 bg-gradient-to-br from-gray-300 to-gray-500 rounded-full p-2 shadow-lg">
          <Award className="w-4 h-4 text-white" />
        </div>
      );
    }
    return null;
  };

  const periodLabels = {
    daily: 'Últimas 24h',
    weekly: 'Últimos 7 dias',
    monthly: 'Últimos 30 dias'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <div className="bg-white bg-opacity-20 backdrop-blur-sm p-4 rounded-2xl">
              <Flame className="w-12 h-12 text-yellow-300" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-2">
                🇧🇷 Tendências do Brasil
              </h1>
              <p className="text-xl text-purple-100">
                Top 50 Letras Mais Visualizadas
              </p>
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-8">
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-5 h-5" />
                  <span className="text-sm">Hoje</span>
                </div>
                <p className="text-3xl font-bold">{stats.today_views.toLocaleString()}</p>
                <p className="text-xs text-purple-200">visualizações</p>
              </div>
              
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-5 h-5" />
                  <span className="text-sm">Esta Semana</span>
                </div>
                <p className="text-3xl font-bold">{stats.week_views.toLocaleString()}</p>
                <p className="text-xs text-purple-200">visualizações</p>
              </div>
              
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Music className="w-5 h-5" />
                  <span className="text-sm">Em Destaque</span>
                </div>
                <p className="text-3xl font-bold">{stats.trending_works_count}</p>
                <p className="text-xs text-purple-200">obras</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Period Tabs */}
        <div className="flex gap-2 mb-8 bg-white rounded-xl p-2 shadow-lg">
          {['daily', 'weekly', 'monthly'].map((p) => (
            <button
              key={p}
              onClick={() => {
                setPeriod(p);
                setActiveTab(p);
              }}
              className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                activeTab === p
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {periodLabels[p]}
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            <p className="mt-4 text-gray-600">Carregando ranking...</p>
          </div>
        )}

        {/* Rankings */}
        {!loading && rankings.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl shadow-lg">
            <Music className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-xl text-gray-600">Nenhuma obra em destaque ainda</p>
            <p className="text-gray-500 mt-2">Seja o primeiro a registrar sua obra!</p>
          </div>
        )}

        {!loading && rankings.length > 0 && (
          <div className="space-y-3">
            {rankings.map((item) => (
              <Link
                key={item.work_id}
                to={`/gallery`}
                className="block bg-white rounded-xl shadow-md hover:shadow-xl transition-all p-4 group"
              >
                <div className="flex items-center gap-4 relative">
                  {/* Ranking Number */}
                  <div className="relative">
                    <div className={`text-4xl font-black w-16 h-16 flex items-center justify-center rounded-xl ${
                      item.rank === 1
                        ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white shadow-lg'
                        : item.rank <= 3
                        ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-white'
                        : item.rank <= 10
                        ? 'bg-gradient-to-br from-purple-100 to-purple-200 text-purple-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {item.rank}
                    </div>
                    {getRankBadge(item.rank)}
                  </div>

                  {/* Work Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 group-hover:text-purple-600 transition-colors">
                          {item.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {item.author_name} • {item.genre}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {getTrendBadge(item.trend, item.growth_percent)}
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        <span className="font-semibold">{item.views.toLocaleString()}</span>
                        <span>views</span>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        {getTrendIcon(item.trend)}
                        <span className="text-xs">
                          {item.trend === 'new' ? 'Nova entrada!' :
                           item.trend === 'up' ? 'Subindo' :
                           item.trend === 'down' ? 'Caindo' :
                           'Estável'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Footer Info */}
        <div className="mt-12 text-center text-gray-500 text-sm">
          <p>🎵 Ranking atualizado em tempo real</p>
          <p className="mt-2">
            Baseado em visualizações de letras na galeria pública
          </p>
        </div>
      </div>
    </div>
  );
};

export default TrendingPage;
